/**
 * pss/carousel — Interactivity API store powering the single-case
 * before/after carousel.
 *
 * Each carousel instance on the page carries its own `data-wp-context`
 * object (current index, total, transition direction, playing flag);
 * the store's actions mutate that context in place and directives in
 * the template react to the changes. Multiple carousels on one page
 * work out-of-the-box — each context is isolated by WP's own store
 * resolution.
 *
 * The server renders every slide; CSS hides non-active ones so
 * SEO/indexing and no-JS users still see the whole case. Two
 * transition modes, picked by `data-transition` on the carousel root:
 *
 *   - "fade" (default): inactive slides at opacity 0, cross-fade.
 *   - "slide": inactive slides translated off-stage left or right of
 *              the active one, animating into place on change.
 *
 * Mode lookup is pure CSS — the store binds `is-active`, `is-before`,
 * and `is-after` regardless of mode and the stylesheet decides what
 * each class means.
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

type CarouselContext = {
    current: number;
    total: number;
    /** 1 = forward, -1 = back — drives slide-direction CSS variant. */
    direction: number;
    /** Last pointerdown X in pixels; null when no gesture in progress. */
    dragStart: number | null;
    /** Set on slide/dot child contexts only; the parent carousel context
     *  doesn't carry it. Used by state.isActive to decide whether the
     *  current slide/dot is the visible/selected one. */
    index?: number;
};

const SWIPE_THRESHOLD_PX = 40;

store('pss/carousel', {
    state: {
        get isFirst(): boolean {
            const ctx = getContext<CarouselContext>();
            return ctx.current === 0;
        },
        get isLast(): boolean {
            const ctx = getContext<CarouselContext>();
            return ctx.current >= ctx.total - 1;
        },
        // Read on each slide/dot — the nested data-wp-context supplies
        // `index`, the parent carousel context supplies `current`.
        get isActive(): boolean {
            const ctx = getContext<CarouselContext>();
            return typeof ctx.index === 'number' && ctx.index === ctx.current;
        },

        // Slide-mode positioning. In fade mode these classes are
        // unstyled (no-op); in slide mode the CSS uses them to translate
        // non-active slides off-stage to the left or right of the
        // current one. Index-relative (not direction-relative) so
        // thumb-jumps animate identically to stepping.
        get isBefore(): boolean {
            const ctx = getContext<CarouselContext>();
            return typeof ctx.index === 'number' && ctx.index < ctx.current;
        },
        get isAfter(): boolean {
            const ctx = getContext<CarouselContext>();
            return typeof ctx.index === 'number' && ctx.index > ctx.current;
        },
    },

    actions: {
        next() {
            const ctx = getContext<CarouselContext>();
            if (ctx.current < ctx.total - 1) {
                ctx.direction = 1;
                ctx.current += 1;
            }
        },

        previous() {
            const ctx = getContext<CarouselContext>();
            if (ctx.current > 0) {
                ctx.direction = -1;
                ctx.current -= 1;
            }
        },

        goto(event: Event) {
            const el = event.currentTarget as HTMLElement | null;
            if (!el) return;
            const index = Number.parseInt(el.dataset.index ?? '', 10);
            if (!Number.isFinite(index)) return;
            const ctx = getContext<CarouselContext>();
            ctx.direction = index > ctx.current ? 1 : -1;
            ctx.current = Math.max(0, Math.min(ctx.total - 1, index));
        },

        onKeydown(event: KeyboardEvent) {
            // Only handle keys when the carousel (or a descendant) has
            // focus; otherwise arrow keys on the page would scrub
            // through photo pairs against the visitor's intent.
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                (this as unknown as { actions: { next: () => void } }).actions.next();
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                (this as unknown as { actions: { previous: () => void } }).actions.previous();
            }
        },

        onPointerDown(event: PointerEvent) {
            const ctx = getContext<CarouselContext>();
            ctx.dragStart = event.clientX;
        },

        onPointerUp(event: PointerEvent) {
            const ctx = getContext<CarouselContext>();
            if (ctx.dragStart === null) return;
            const dx = event.clientX - ctx.dragStart;
            ctx.dragStart = null;
            if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
            const self = this as unknown as { actions: { next: () => void; previous: () => void } };
            if (dx < 0) {
                self.actions.next();
            } else {
                self.actions.previous();
            }
        },

        onPointerCancel() {
            const ctx = getContext<CarouselContext>();
            ctx.dragStart = null;
        },
    },

    callbacks: {
        // Runs once per instance on mount; sticks focusability on the
        // root so keyboard navigation works without the visitor having
        // to tab through every slide first.
        onInit() {
            const { ref } = getElement();
            if (ref instanceof HTMLElement && !ref.hasAttribute('tabindex')) {
                ref.setAttribute('tabindex', '0');
            }
        },

        // Re-runs whenever any reactive state read inside it changes.
        // Used by the aside layout's thumb strip: when a thumb becomes
        // active (its index === parent.current) we pull it into view in
        // its scroll container so the visitor never loses sight of the
        // current pair while clicking through a long list.
        onActiveScroll() {
            const ctx = getContext<CarouselContext>();
            if (typeof ctx.index !== 'number' || ctx.index !== ctx.current) {
                return;
            }
            // Skip the initial mount-fire: the server already renders the
            // first thumb as active, and a `scrollIntoView` on page load
            // would scroll the *document* to the strip's mid-page position.
            // `direction` only leaves 0 once the visitor steps a slide.
            if (ctx.direction === 0) {
                return;
            }
            const { ref } = getElement();
            if (ref instanceof HTMLElement) {
                ref.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            }
        },
    },
});
